import { getAddress } from 'viem';
import type { StateCreator } from 'zustand';
import type { Account, Wallet } from '@/api/spec';
import type { RootState } from '../rootStore.ts';

// user's wallet representation by DEX. one wallet can have one account per exchange.
export interface DexWalletSlice {
  walletData?: Omit<Wallet, 'as'>;
  account?: Account;
  /** The first WebSocket wallet snapshot of this session has landed; it carries the account (or its absence). */
  walletSnapshotReceived: boolean;
  lastRequestId?: number;
  lastLegacyRequestId?: number;

  isDelegated: boolean;
  isDelegatedOwner: boolean;
  activeDelegatedAddress?: string;
  connectedWalletAddress?: string;
  setConnectedWalletAddress: (address: string | undefined) => void;
  setDelegatedOwner: (isOwner: boolean) => void;
  setWalletSnapshot: (wallet: Wallet) => void;
  patchWallet: (walletPatch: Partial<Wallet>) => void;
  patchAccount: (accountPatch: Account) => void;
  resetWallet: () => void;
  generateRequestId: () => number;
}

export const createWalletSlice: StateCreator<RootState, [['zustand/immer', never]], [], DexWalletSlice> = (set, get) => ({
  walletData: undefined,
  account: undefined,
  walletSnapshotReceived: false,
  lastRequestId: undefined,
  lastLegacyRequestId: undefined,
  isDelegated: false,
  isDelegatedOwner: false,
  activeDelegatedAddress: undefined,
  connectedWalletAddress: undefined,
  setConnectedWalletAddress: (address) => {
    set((state) => {
      state.connectedWalletAddress = address;
    });
  },
  setDelegatedOwner: (isOwner) => {
    set((state) => {
      state.isDelegatedOwner = isOwner;
    });
  },
  setWalletSnapshot: (wallet) => {
    set((state) => {
      const { as, ...walletData } = wallet;
      state.walletData = walletData;
      state.walletSnapshotReceived = true;

      // Delegated Account Detection
      const snapshotAddress = wallet.addr;
      const connectedAddress = get().connectedWalletAddress;

      if (snapshotAddress && connectedAddress && getAddress(snapshotAddress) !== getAddress(connectedAddress)) {
        state.isDelegated = true;
        state.activeDelegatedAddress = getAddress(snapshotAddress);
      } else {
        state.isDelegated = false;
        state.isDelegatedOwner = false;
        state.activeDelegatedAddress = undefined;
      }

      const currentExchangeId = get().exchange?.contract.id;
      if (currentExchangeId && as) {
        const account = as.find((acc) => acc.in === currentExchangeId);
        state.account = account;
        if (account) {
          state.lastRequestId = Math.max(state.lastRequestId || 0, account.lfr);
          if (typeof window !== 'undefined') window.dexAccountId = account.id;
        }
      } else {
        state.account = undefined;
        state.lastRequestId = undefined;
        if (typeof window !== 'undefined') window.dexAccountId = undefined;
      }
    });
  },
  patchWallet: (walletPatch) => {
    set((state) => {
      const { as, ...restOfPatch } = walletPatch;
      if (state.walletData) {
        Object.assign(state.walletData, restOfPatch);
      } else if (Object.keys(restOfPatch).length > 0) {
        state.walletData = restOfPatch as Omit<Wallet, 'as'>;
      }

      if (as) {
        const currentExchangeId = get().exchange?.contract.id;
        const accountToPatch = as.find((acc) => acc.in === currentExchangeId);
        if (currentExchangeId && accountToPatch) {
          if (state.account) {
            Object.assign(state.account, accountToPatch);
          } else {
            state.account = accountToPatch;
          }
          if (typeof window !== 'undefined') window.dexAccountId = state.account.id;
        }
      }
    });
  },
  patchAccount: (accountPatch) => {
    set((state) => {
      const currentExchangeId = get().exchange?.contract.id;

      if (state.account && state.account.in === accountPatch.in) {
        Object.assign(state.account, accountPatch);
        if (accountPatch.lfr != null) {
          state.lastRequestId = Math.max(state.lastRequestId || 0, accountPatch.lfr);
        }
        if (typeof window !== 'undefined') window.dexAccountId = state.account.id;
      } else if (!state.account && currentExchangeId && accountPatch.in === currentExchangeId) {
        state.account = accountPatch;
        if (typeof window !== 'undefined') window.dexAccountId = state.account.id;
        if (typeof accountPatch.lfr === 'number') {
          state.lastRequestId = accountPatch.lfr;
        }
      }
    });
  },
  resetWallet: () => {
    set((state) => {
      state.walletData = undefined;
      state.account = undefined;
      state.walletSnapshotReceived = false;
      state.lastRequestId = undefined;
      state.lastLegacyRequestId = undefined;
      state.isDelegated = false;
      state.isDelegatedOwner = false;
      state.activeDelegatedAddress = undefined;
      if (typeof window !== 'undefined') window.dexAccountId = undefined;
    });
  },
  generateRequestId: () => {
    let nextId = 0;
    const now = Date.now();

    set((state) => {
      if (state.account?.fw) {
        const serverLfr = state.account?.lfr || 0;
        const currentLocal = state.lastRequestId || 0;
        nextId = Math.max(currentLocal, serverLfr) + 1;
        state.lastRequestId = nextId;
      } else {
        const currentLegacy = state.lastLegacyRequestId || 0;
        if (now > currentLegacy) {
          nextId = now;
        } else {
          nextId = currentLegacy + 1;
        }
        state.lastLegacyRequestId = nextId;
      }
    });
    return nextId;
  },
});
